
import React from 'react';
import { FaceLivenessDetectorCore, AwsCredentialProvider } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useDispatch } from 'react-redux'
import { livenessValidation } from '@/domains/auth/redux/authenticationReactive';
import { LivenessStep, markLivenessStep } from '@/shared/helpers/liveness-step-tracker.helper';
import { reportEvent } from '@/shared/helpers/report-event.helper';
import { LogType } from '@/shared/types/logType';

interface LivenessQuickStartReactInternalProps {
    livenessSessionId: string;
    awsSessionId: string;
    region: string;
    disableStartScreen?: boolean;
    // Telemetry context only; the component behaves the same without them.
    vendor?: string;
    personUuid?: string;
    engineVersion?: 'aws-v1' | 'aws-v2';
    // `cause` is the browser's getUserMedia error — NotAllowedError (user
    // denied), NotReadableError ("Could not start video source": camera busy
    // in another app), NotFoundError (no camera), OverconstrainedError.
    onCameraAccessError?: (cause: { errorName: string | null; errorMessage: string | null }) => void;
}

function LivenessQuickStartReact({credentials, livenessSessionId, awsSessionId, region, disableStartScreen, onCameraAccessError, vendor, personUuid, engineVersion}: {credentials: any} & LivenessQuickStartReactInternalProps) {
    
    const dispatch = useDispatch();

    // Amplify only exposes three callbacks (analysis complete, error, user
    // cancel); all of them carry the selected camera's DeviceInfo, which is
    // otherwise invisible to us. Labels are the browser's camera names.
    const report = (event: string, type: LogType, extra: Record<string, unknown>, deviceInfo?: { deviceId?: string; groupId?: string; label?: string }) => {
        reportEvent(event, {
            event,
            vendor,
            engineVersion,
            livenessSessionId,
            awsSessionId,
            personUuid,
            cameraLabel: deviceInfo?.label,
            cameraDeviceId: deviceInfo?.deviceId,
            timestamp: new Date().toISOString(),
            ...extra,
        }, type);
    };
    const credentialProvider: AwsCredentialProvider = async () => {
        // Get the credentials from the liveness-handler lambda
        return credentials
    }

    const handleAnalysisComplete: (deviceInfo?: any) => Promise<void> = async (deviceInfo) => {
        report('LIVENESS_AWS_ANALYSIS_COMPLETE', LogType.INFO, {}, deviceInfo);
        // Amplify has no "selfie taken" callback; analysis-complete fires once
        // the video check is captured and uploaded, which is the closest point.
        markLivenessStep(LivenessStep.CAPTURE_COMPLETED);
        dispatch(livenessValidation({ liveness: true, data: { session_id:awsSessionId, liveness_session_id: livenessSessionId } }));
    };

    const handleError = async (error: any, deviceInfo?: any) => {
        // Amplify's LivenessError wraps the real cause: `state` is the identifier
        // (e.g. SERVER_ERROR) and `error` is an Error carrying "the more specific
        // error name and stack trace" (per Amplify docs). The name is the reliably
        // populated field, so fold it into the message forwarded to persons-api.
        const underlyingError = error?.error ?? null;
        const errorState = error?.state ?? null;
        const errorStatus = errorState ?? error?.code ?? error?.name ?? null;
        const errorMessage = underlyingError
            ? [underlyingError.name, underlyingError.message].filter(Boolean).join(': ')
            : (error?.message ?? 'Liveness verification failed');

        // Camera-access errors are reported by the parent as
        // LIVENESS_AWS_CAMERA_ACCESS_ERROR; everything else is reported here.
        if (errorState !== 'CAMERA_ACCESS_ERROR') {
            report(`LIVENESS_AWS_ERROR: ${errorStatus ?? 'UNKNOWN'}`, LogType.ERROR, {
                awsErrorState: errorState,
                errorStatus,
                errorMessage,
            }, deviceInfo);
        }

        // CAMERA_ACCESS_ERROR is user-fixable; skip Redux so LivenessWrapper does not redirect.
        if (errorState === 'CAMERA_ACCESS_ERROR' && onCameraAccessError) {
            onCameraAccessError({
                errorName: underlyingError?.name ?? null,
                errorMessage: underlyingError?.message ?? null,
            });
            return;
        }
        dispatch(livenessValidation({
            liveness: false,
            data: {
                session_id: awsSessionId,
                liveness_session_id: livenessSessionId,
                error: true,
                error_status: errorStatus,
                error_message: errorMessage,
            },
        }));
    };

    const handleUserCancel = () => {
        report('LIVENESS_AWS_USER_CANCELLED', LogType.WARN, {});
        dispatch(livenessValidation({
            liveness: false,
            data: {
                session_id: awsSessionId,
                liveness_session_id: livenessSessionId,
                error: true,
                error_status: 'USER_CANCELLED',
                error_message: 'The user cancelled AWS liveness verification.',
            },
        }));
    };

    return (
        <ThemeProvider>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                <FaceLivenessDetectorCore
                    sessionId={awsSessionId}
                    region={region}
                    onAnalysisComplete={handleAnalysisComplete}
                    onError={handleError}
                    onUserCancel={handleUserCancel}
                    config={{ credentialProvider }}
                    disableStartScreen={disableStartScreen}
                />
            </div>
        </ThemeProvider>
    );
}

export default LivenessQuickStartReact;
